import { useCallback, useEffect, useState } from "react";

/**
 * useTheme — the one place the colour theme is chosen (Fontaine M02).
 *
 * Three states, like the OS: "system" (follow prefers-color-scheme), "dark", "light".
 * The choice is persisted in localStorage and applied as `data-theme` on <html>:
 *   - "dark"  → <html data-theme="dark">   (forces dark even on a light OS)
 *   - "light" → <html data-theme="light">
 *   - "system"→ no attribute; the generated tokens.css follows prefers-color-scheme
 * Dark is the product default: with no attribute and no OS light preference, dark renders.
 *
 * @returns {{ theme: "system"|"dark"|"light", resolved: "dark"|"light", setTheme: (t: string) => void, cycle: () => void }}
 */
export const THEME_KEY = "rc-theme";
export const THEMES = ["system", "dark", "light"];

const readStored = () => {
  try {
    const v = window.localStorage.getItem(THEME_KEY);
    return THEMES.includes(v) ? v : "system";
  } catch {
    return "system";
  }
};

const systemPrefersLight = () =>
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-color-scheme: light)").matches
    : false;

export const applyTheme = (theme) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (theme === "dark" || theme === "light") root.dataset.theme = theme;
  else delete root.dataset.theme;
};

export default function useTheme() {
  const [theme, setThemeState] = useState(readStored);
  const [osLight, setOsLight] = useState(systemPrefersLight);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = (e) => setOsLight(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  const setTheme = useCallback((next) => {
    const value = THEMES.includes(next) ? next : "system";
    setThemeState(value);
    try {
      window.localStorage.setItem(THEME_KEY, value);
    } catch {
      /* storage unavailable — theme still applies for this session */
    }
  }, []);

  const cycle = useCallback(() => {
    setTheme(THEMES[(THEMES.indexOf(theme) + 1) % THEMES.length]);
  }, [theme, setTheme]);

  const resolved = theme === "system" ? (osLight ? "light" : "dark") : theme;
  return { theme, resolved, setTheme, cycle };
}
